import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Bell, MapPin, ChevronDown, Navigation } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { api } from '../lib/api';
import t from '../translations';
import ZodiacProfileCard from '../components/ZodiacProfileCard';

/* ── Muhurta quality badge ── */
function QualityBadge({ quality, tr }) {
  const cfg = {
    excellent: { bg: 'rgba(45,106,79,0.15)', border: 'rgba(45,106,79,0.4)', color: '#2D6A4F', label: tr.excellent },
    good:      { bg: 'rgba(201,168,76,0.1)',  border: 'rgba(201,168,76,0.3)', color: '#C9A84C', label: tr.good },
    neutral:   { bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.1)', color: '#8A8580', label: tr.neutral },
    avoid:     { bg: 'rgba(192,57,43,0.1)',    border: 'rgba(192,57,43,0.3)',   color: '#C0392B', label: tr.avoid },
  };
  const c = cfg[quality] || cfg.neutral;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
      background: c.bg, border: `1px solid ${c.border}`, color: c.color,
      letterSpacing: '0.06em', textTransform: 'uppercase',
    }}>
      {c.label}
    </span>
  );
}

/* ── Single Muhurta row ── */
function MuhurtaRow({ label, data, tr }) {
  if (!data) return null;
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '10px 0',
      borderBottom: '1px solid rgba(255,255,255,0.05)',
    }}>
      <div>
        <p style={{ fontSize: 13, fontWeight: 600, color: '#F5F0E8', marginBottom: 2 }}>{label}</p>
        <p style={{ fontSize: 11, color: '#8A8580' }}>{data.start} – {data.end}</p>
      </div>
      <QualityBadge quality={data.quality} tr={tr} />
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { profile, refreshProfile } = useAuth();
  const { lang } = useLang();
  const tr = t[lang];

  const [showAlarmModal,     setShowAlarmModal]     = useState(false);
  const [alarmTime,          setAlarmTime]          = useState('07:00');
  const [savingAlarm,        setSavingAlarm]        = useState(false);
  const [revealing,          setRevealing]          = useState(false);

  // Location & Muhurtas
  const [muhurtas,           setMuhurtas]           = useState(null);
  const [fetchingMuhurtas,   setFetchingMuhurtas]   = useState(false);
  const [showLocationModal,  setShowLocationModal]  = useState(false);
  const [locationQuery,      setLocationQuery]      = useState('');
  const [locationCities,     setLocationCities]     = useState([]);
  const [searchingLoc,       setSearchingLoc]       = useState(false);
  const [showCgExpanded,     setShowCgExpanded]     = useState(false);
  const [detectingLoc,       setDetectingLoc]       = useState(false);

  // Cosmic Snapshot removed — now on MyChartPage
  // Lucky Number
  const [luckyNum,          setLuckyNum]           = useState(null);

  const notifTimerRef = useRef(null);

  // Initialize from profile
  useEffect(() => {
    if (!profile) return;

    // Alarm modal: show once if not set
    const shown = localStorage.getItem('dr_alarm_asked');
    if (!shown && !profile.cosmicAlarmTime) {
      setShowAlarmModal(true);
      localStorage.setItem('dr_alarm_asked', '1');
    }
    if (profile.cosmicAlarmTime) {
      setAlarmTime(profile.cosmicAlarmTime);
      scheduleNotification(profile.cosmicAlarmTime, profile.name);
    }

    // Location: if saved, fetch muhurtas; else prompt
    if (profile.currentLat && profile.currentLng) {
      fetchMuhurtasData(profile.currentLat, profile.currentLng);
    } else {
      const locShown = localStorage.getItem('dr_location_asked');
      if (!locShown) {
        setTimeout(() => setShowLocationModal(true), 800);
        localStorage.setItem('dr_location_asked', '1');
      }
    }

    // Fetch lucky number
    api.get('/api/lucky-number').then(d => setLuckyNum(d)).catch(() => {});

    return () => { if (notifTimerRef.current) clearTimeout(notifTimerRef.current); };
  }, [profile]);

  const scheduleNotification = (time, name) => {
    // Try Service Worker first (survives page navigation)
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SET_ALARM', time, name: name || '' });
    }
    // Fallback: setTimeout in current tab
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const [h, m] = time.split(':').map(Number);
    const now    = new Date();
    const target = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0);
    if (target <= now) return;
    const ms = target.getTime() - now.getTime();
    if (notifTimerRef.current) clearTimeout(notifTimerRef.current);
    notifTimerRef.current = setTimeout(() => {
      new Notification(tr.appName, {
        body: `${name || ''}, ${tr.tagline} — ${tr.revealReading}`,
        icon: '/favicon.ico',
      });
    }, ms);
  };

  const fetchMuhurtasData = async (lat, lng) => {
    setFetchingMuhurtas(true);
    try {
      const data = await api.post('/api/muhurtas', { lat, lng });
      setMuhurtas(data);
    } catch { /* silent */ }
    setFetchingMuhurtas(false);
  };

  // Auto-detect location
  const handleAutoDetect = () => {
    if (!navigator.geolocation) { setDetectingLoc(false); return; }
    setDetectingLoc(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setDetectingLoc(false);
        setShowLocationModal(false);
        fetchMuhurtasData(pos.coords.latitude, pos.coords.longitude);
      },
      () => { setDetectingLoc(false); },
      { timeout: 10000 }
    );
  };

  // Manual location search (Nominatim)
  useEffect(() => {
    if (locationQuery.length < 2) { setLocationCities([]); return; }
    const timer = setTimeout(async () => {
      setSearchingLoc(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(locationQuery)}&format=json&limit=5&addressdetails=1&countrycodes=in`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const data = await res.json();
        setLocationCities(data.filter(d => d.lat && d.lon).map(d => {
          const a = d.address || {};
          return {
            name: [a.city || a.town || a.village || a.county, a.state].filter(Boolean).join(', '),
            lat: parseFloat(d.lat), lng: parseFloat(d.lon),
          };
        }));
      } catch { /* silent */ }
      setSearchingLoc(false);
    }, 450);
    return () => clearTimeout(timer);
  }, [locationQuery]);

  const pickLocationCity = (city) => {
    setShowLocationModal(false);
    setLocationQuery('');
    setLocationCities([]);
    fetchMuhurtasData(city.lat, city.lng);
  };

  const registerPushSubscription = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
      const { publicKey } = await api.get('/api/push/vapid-key');
      if (!publicKey) return;
      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      const sub = existing || await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: publicKey,
      });
      if (sub) {
        const json = sub.toJSON();
        await api.post('/api/push/subscribe', {
          endpoint: json.endpoint,
          keys: json.keys,
        });
      }
    } catch { /* silent — push not supported */ }
  };

  const handleSetAlarm = async () => {
    if (!alarmTime) return;
    setSavingAlarm(true);
    try {
      if ('Notification' in window && Notification.permission === 'default') {
        await Notification.requestPermission();
      }
      await api.put('/api/user/settings', { cosmicAlarmTime: alarmTime });
      await refreshProfile();
      scheduleNotification(alarmTime, profile?.name);
      // Register Web Push subscription for server-sent push
      await registerPushSubscription();
    } catch { /* silent */ }
    setSavingAlarm(false);
    setShowAlarmModal(false);
  };

  const handleReveal = async () => {
    setRevealing(true);
    try {
      const data = await api.get('/api/reading/today');
      navigate('/reading', { state: { reading: data } });
    } catch (err) {
      if (err.status === 402) navigate('/paywall');
      else alert('Failed to load reading. Please try again.');
    }
    setRevealing(false);
  };

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return lang === 'hi' ? 'सुप्रभात' : 'Good Morning';
    if (h < 17) return lang === 'hi' ? 'नमस्ते' : 'Good Afternoon';
    return lang === 'hi' ? 'शुभ संध्या' : 'Good Evening';
  };

  if (!profile) return null;

  const hasMuhurtas = !!muhurtas;

  return (
    <div data-testid="home-page" style={{ minHeight: '100vh', background: '#0A0F1E', paddingBottom: 48 }}>

      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '56px 20px 20px' }}>
        <div>
          <p style={{ fontSize: 12, color: '#8A8580', marginBottom: 2 }}>{greeting()}</p>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: '#F5F0E8' }}>
            {profile.name || tr.appName}
          </h2>
        </div>
        <button
          data-testid="settings-btn"
          onClick={() => navigate('/settings')}
          style={{
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.15)',
            borderRadius: 12, padding: 10, cursor: 'pointer', color: '#C9A84C',
          }}
        >
          <Settings size={20} />
        </button>
      </div>

      <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Zodiac Profile Card — simplified (Sun/Moon/Asc only) */}
        <div className="fade-in-up">
          <ZodiacProfileCard profile={profile} simplified={true} />
        </div>

        {/* No birth time notice */}
        {profile.birthDate && !profile.birthTime && (
          <div className="fade-in-up-d1" style={{
            padding: '12px 16px', background: 'rgba(201,168,76,0.07)',
            border: '1px solid rgba(201,168,76,0.15)', borderRadius: 12,
          }}>
            <p style={{ fontSize: 12, color: '#8A8580' }}>✦ {tr.noTimeMessage}</p>
          </div>
        )}

        {/* Location prompt card (if no location saved and no muhurtas) */}
        {!hasMuhurtas && !fetchingMuhurtas && (
          <div className="card fade-in-up-d1" style={{ borderColor: 'rgba(201,168,76,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <MapPin size={18} color="#C9A84C" />
              </div>
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#F5F0E8', marginBottom: 2 }}>{tr.setLocation}</p>
                <p style={{ fontSize: 11, color: '#8A8580' }}>{tr.locationSubtitle}</p>
              </div>
            </div>
            <button
              data-testid="set-location-btn"
              onClick={() => setShowLocationModal(true)}
              className="btn-secondary"
              style={{ fontSize: 13, padding: '10px 0' }}
            >
              {tr.setLocation}
            </button>
          </div>
        )}

        {/* Muhurtas card */}
        {fetchingMuhurtas && (
          <div className="card fade-in-up-d1" style={{ textAlign: 'center', padding: 24 }}>
            <p style={{ fontSize: 13, color: '#8A8580' }}>{tr.loading}</p>
          </div>
        )}

        {hasMuhurtas && (
          <div className="card fade-in-up-d1" data-testid="muhurtas-card" style={{ padding: 0, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{
              padding: '16px 20px 12px',
              borderBottom: '1px solid rgba(201,168,76,0.1)',
              background: 'rgba(201,168,76,0.03)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{
                    fontSize: 11, color: '#C9A84C', letterSpacing: '0.1em',
                    textTransform: 'uppercase', fontWeight: 700, marginBottom: 4,
                  }}>
                    {tr.muhurtasTitle}
                  </p>
                  <p style={{ fontSize: 11, color: '#8A8580' }}>
                    {tr.sunrise}: {muhurtas.sunrise} &nbsp;·&nbsp; {tr.sunset}: {muhurtas.sunset}
                  </p>
                </div>
                <button
                  data-testid="refresh-location-btn"
                  onClick={() => setShowLocationModal(true)}
                  style={{
                    background: 'none', border: 'none',
                    color: '#8A8580', cursor: 'pointer', padding: 4,
                  }}
                >
                  <MapPin size={14} />
                </button>
              </div>
            </div>

            <div style={{ padding: '4px 20px 8px' }}>
              {/* Current Chaughadiya */}
              {muhurtas.currentChaughadiya && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 0',
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                }}>
                  <div>
                    <p style={{ fontSize: 11, color: '#8A8580', marginBottom: 2 }}>
                      {tr.currentPeriod} ({tr.chaughadiya})
                    </p>
                    <p style={{ fontSize: 15, fontWeight: 700, color: '#F5F0E8' }}>
                      {muhurtas.currentChaughadiya.name}
                    </p>
                    <p style={{ fontSize: 11, color: '#8A8580' }}>
                      {muhurtas.currentChaughadiya.start} – {muhurtas.currentChaughadiya.end}
                    </p>
                  </div>
                  <QualityBadge quality={muhurtas.currentChaughadiya.quality} tr={tr} />
                </div>
              )}

              <MuhurtaRow label={tr.brahmaMuhurta}  data={muhurtas.brahmaMuhurta}  tr={tr} />
              <MuhurtaRow label={tr.abhijitMuhurta} data={muhurtas.abhijitMuhurta} tr={tr} />
              <MuhurtaRow label={tr.rahuKaal}       data={muhurtas.rahuKaal}       tr={tr} />

              {/* Expand Chaughadiya schedule */}
              <button
                data-testid="toggle-chaughadiya-btn"
                onClick={() => setShowCgExpanded(v => !v)}
                style={{
                  width: '100%', background: 'none', border: 'none',
                  color: '#C9A84C', cursor: 'pointer', fontSize: 12,
                  fontWeight: 600, padding: '10px 0 4px',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <ChevronDown size={14} style={{
                  transform: showCgExpanded ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s',
                }} />
                {tr.chaughadiya} {tr.dayChaughadiya} / {tr.nightChaughadiya}
              </button>

              <div style={{
                maxHeight: showCgExpanded ? '600px' : '0px',
                overflow: 'hidden', transition: 'max-height 0.3s ease',
              }}>
                {/* Day chaughadiyas */}
                <p style={{ fontSize: 10, color: '#8A8580', textTransform: 'uppercase',
                  letterSpacing: '0.08em', marginTop: 8, marginBottom: 4, fontWeight: 600 }}>
                  {tr.dayChaughadiya}
                </p>
                {muhurtas.dayChaughadiyas?.map((cg, i) => (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 0',
                  }}>
                    <div>
                      <span style={{ fontSize: 12, color: '#F5F0E8', fontWeight: 600 }}>{cg.name}</span>
                      <span style={{ fontSize: 11, color: '#8A8580', marginLeft: 8 }}>{cg.start}–{cg.end}</span>
                    </div>
                    <QualityBadge quality={cg.quality} tr={tr} />
                  </div>
                ))}
                {/* Night chaughadiyas */}
                <p style={{ fontSize: 10, color: '#8A8580', textTransform: 'uppercase',
                  letterSpacing: '0.08em', marginTop: 10, marginBottom: 4, fontWeight: 600 }}>
                  {tr.nightChaughadiya}
                </p>
                {muhurtas.nightChaughadiyas?.map((cg, i) => (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 0',
                  }}>
                    <div>
                      <span style={{ fontSize: 12, color: '#F5F0E8', fontWeight: 600 }}>{cg.name}</span>
                      <span style={{ fontSize: 11, color: '#8A8580', marginLeft: 8 }}>{cg.start}–{cg.end}</span>
                    </div>
                    <QualityBadge quality={cg.quality} tr={tr} />
                  </div>
                ))}
                <div style={{ height: 8 }} />
              </div>
            </div>
          </div>
        )}

        {/* Cosmic Alarm */}
        <div className="card fade-in-up-d3" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 12, color: '#C9A84C', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
              {tr.cosmicAlarmTitle}
            </p>
            {profile.cosmicAlarmTime ? (
              <p style={{ fontSize: 13, color: '#F5F0E8' }}>
                {tr.alarmSet} <strong>{profile.cosmicAlarmTime}</strong>
              </p>
            ) : (
              <p style={{ fontSize: 12, color: '#8A8580' }}>{tr.cosmicAlarmPrompt}</p>
            )}
          </div>
          <button
            data-testid="alarm-icon-btn"
            onClick={() => { setAlarmTime(profile.cosmicAlarmTime || '07:00'); setShowAlarmModal(true); }}
            style={{
              background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.2)',
              borderRadius: 10, padding: 10, cursor: 'pointer', color: '#C9A84C', flexShrink: 0,
            }}
          >
            <Bell size={18} />
          </button>
        </div>

        {/* ── Lucky Number ── */}
        {luckyNum && (
          <div
            data-testid="lucky-number-card"
            className="card fade-in-up-d3"
            style={{
              background: `linear-gradient(135deg, ${luckyNum.hexColor}18 0%, rgba(201,168,76,0.04) 100%)`,
              border: `1px solid ${luckyNum.hexColor}44`,
              cursor: 'pointer',
            }}
            onClick={() => {
              if (navigator.share) {
                navigator.share({ title: 'DailyRaashi', text: `My Vedic lucky number today is ${luckyNum.number} — ruled by ${luckyNum.planet}! ${luckyNum.mantra}` }).catch(() => {});
              }
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {/* Big number */}
              <div style={{
                width: 64, height: 64, borderRadius: 18, flexShrink: 0,
                background: `${luckyNum.hexColor}22`, border: `2px solid ${luckyNum.hexColor}55`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ fontSize: 32, fontWeight: 900, color: luckyNum.hexColor, fontFamily: "'Playfair Display', serif", lineHeight: 1 }}>
                  {luckyNum.number}
                </span>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 11, color: '#8A8580', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
                  {tr.luckyNumber}
                </p>
                <p style={{ fontSize: 14, fontWeight: 600, color: '#F5F0E8', marginBottom: 4 }}>
                  {luckyNum.planet} · {luckyNum.direction}
                </p>
                <p style={{ fontSize: 12, color: luckyNum.hexColor, fontFamily: "'Noto Sans Devanagari', serif" }}>
                  {luckyNum.mantra}
                </p>
              </div>
              <div style={{ fontSize: 16, color: '#8A8580', flexShrink: 0 }}>↗</div>
            </div>
          </div>
        )}

        {/* Reveal CTA */}
        <div className="fade-in-up-d4" style={{ textAlign: 'center', marginTop: 8 }}>
          <p style={{ fontSize: 12, color: '#8A8580', marginBottom: 16, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            {tr.todaysReading}
          </p>
          <button
            data-testid="reveal-reading-btn"
            onClick={handleReveal}
            disabled={revealing}
            className="btn-primary pulse-gold"
            style={{ maxWidth: 300, margin: '0 auto', display: 'block', fontSize: 16, padding: '18px 32px' }}
          >
            {revealing ? tr.generatingReading : tr.revealReading}
          </button>
        </div>

      </div>

      {/* ── Cosmic Alarm Modal ── */}
      {showAlarmModal && (
        <div
          data-testid="cosmic-alarm-modal"
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(8,12,26,0.92)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            backdropFilter: 'blur(4px)',
          }}
          onClick={e => e.target === e.currentTarget && setShowAlarmModal(false)}
        >
          <div className="fade-in-up" style={{
            background: '#111827', border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: '20px 20px 0 0', padding: '32px 24px 40px',
            width: '100%', maxWidth: 430,
          }}>
            <div style={{ width: 40, height: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 2, margin: '0 auto 28px' }} />
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🔔</div>
              <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: '#F5F0E8', fontWeight: 700, marginBottom: 8 }}>
                {tr.cosmicAlarmTitle}
              </h3>
              <p style={{ fontSize: 13, color: '#8A8580', lineHeight: 1.6, marginBottom: 4 }}>
                {tr.cosmicAlarmPrompt}
              </p>
              <p style={{ fontSize: 12, color: 'rgba(138,133,128,0.7)', lineHeight: 1.6 }}>
                {tr.cosmicAlarmExplain}
              </p>
            </div>
            <input
              data-testid="alarm-time-input"
              type="time"
              className="input-field"
              value={alarmTime}
              onChange={e => setAlarmTime(e.target.value)}
              style={{ marginBottom: 20, textAlign: 'center', fontSize: 24, letterSpacing: '0.1em', fontWeight: 600 }}
            />
            <button
              data-testid="set-alarm-btn"
              className="btn-primary"
              onClick={handleSetAlarm}
              disabled={!alarmTime || savingAlarm}
              style={{ marginBottom: 12 }}
            >
              {savingAlarm ? tr.loading : tr.setAlarm}
            </button>
            <button data-testid="skip-alarm-btn" className="btn-secondary" onClick={() => setShowAlarmModal(false)}>
              {tr.skipAlarm}
            </button>
          </div>
        </div>
      )}

      {/* ── Location Modal ── */}
      {showLocationModal && (
        <div
          data-testid="location-modal"
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(8,12,26,0.92)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            backdropFilter: 'blur(4px)',
          }}
          onClick={e => e.target === e.currentTarget && setShowLocationModal(false)}
        >
          <div className="fade-in-up" style={{
            background: '#111827', border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: '20px 20px 0 0', padding: '32px 24px 40px',
            width: '100%', maxWidth: 430,
          }}>
            <div style={{ width: 40, height: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 2, margin: '0 auto 28px' }} />

            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>📍</div>
              <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: '#F5F0E8', fontWeight: 700, marginBottom: 8 }}>
                {tr.setLocation}
              </h3>
              <p style={{ fontSize: 13, color: '#8A8580', lineHeight: 1.6 }}>
                {tr.locationSubtitle}
              </p>
            </div>

            {/* Auto-detect */}
            <button
              data-testid="auto-detect-location-btn"
              className="btn-primary"
              onClick={handleAutoDetect}
              disabled={detectingLoc}
              style={{ marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              <Navigation size={16} />
              {detectingLoc ? tr.detectingLocation : tr.useMyLocation}
            </button>

            {/* Manual city search */}
            <div style={{ position: 'relative', marginBottom: 14 }}>
              <input
                data-testid="location-city-input"
                type="text"
                className="input-field"
                placeholder={tr.enterCity}
                value={locationQuery}
                onChange={e => setLocationQuery(e.target.value)}
                autoComplete="off"
              />
              {searchingLoc && (
                <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: '#8A8580', fontSize: 12 }}>↻</div>
              )}
              {locationCities.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                  background: '#1a2335', border: '1px solid rgba(201,168,76,0.2)',
                  borderRadius: 12, marginTop: 4, overflow: 'hidden',
                }}>
                  {locationCities.map((c, i) => (
                    <button
                      key={i}
                      type="button"
                      onMouseDown={e => { e.preventDefault(); pickLocationCity(c); }}
                      style={{
                        width: '100%', textAlign: 'left', padding: '12px 16px',
                        background: 'transparent', border: 'none',
                        color: '#F5F0E8', cursor: 'pointer', fontSize: 13,
                        borderBottom: i < locationCities.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                      }}
                    >
                      <MapPin size={12} style={{ display: 'inline', marginRight: 6, color: '#C9A84C' }} />
                      {c.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              data-testid="skip-location-btn"
              className="btn-secondary"
              onClick={() => setShowLocationModal(false)}
              style={{ fontSize: 13 }}
            >
              {tr.locationSkip}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
